import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Testimonial } from '@/types/types';
import { testimonialService } from '@/services/testimonialsService';

interface TestimonialContextType {
  testimonials: Testimonial[];
  loading: boolean;
  error: string | null;
  refetchTestimonials: () => Promise<void>;
}

const TestimonialContext = createContext<TestimonialContextType | undefined>(undefined);

export const useTestimonials = () => {
  const context = useContext(TestimonialContext);
  if (context === undefined) {
    throw new Error('useTestimonials must be used within a TestimonialProvider');
  }
  return context;
};

interface TestimonialProviderProps {
  children: ReactNode;
}

export const TestimonialProvider: React.FC<TestimonialProviderProps> = ({ children }) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTestimonials = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await testimonialService.getAllTestimonials();
      setTestimonials(data);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setError('Failed to load testimonials. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const refetchTestimonials = async () => {
    await fetchTestimonials();
  };

  return (
    <TestimonialContext.Provider value={{ testimonials, loading, error, refetchTestimonials }}>
      {children}
    </TestimonialContext.Provider>
  );
};